// v77.99.15 — Feature-page hero copy variants (A/B testing without redeploy).
//
// HOW TO ADD A NEW VARIANT (30-second edit, no redeploy when shipped):
//
//   1. Pick a feature page key — one of:
//        "overview" · "calendar" · "expenses" · "messaging"
//        · "court-bundle" · "journal"
//
//   2. Pick a short variant code (alphanumeric / dashes only). This is
//      what you'll paste into ad URLs as `?utm_content=YOUR_CODE`
//      (or `?utm_campaign=YOUR_CODE`, or `?variant=YOUR_CODE`).
//      Examples: "solicitor-pitch", "mum-direct", "feb-promo-A".
//
//   3. Add an entry under the page key like this:
//        "your-variant-code": {
//          eyebrow: "Optional small uppercase tag above the headline",
//          headline: "Your punchy hero headline.",
//          body: "Calm reassuring detail body. 2-3 sentences max.",
//        }
//      Any field you omit falls back to the page's default.
//
//   4. Build the ad URL:
//        https://app.coparentconsole.com/features/expenses?utm_content=your-variant-code
//
//      Variant lookup priority (first match wins):
//        a) `?variant=...`         — explicit override, easiest for testing
//        b) `?utm_content=...`     — Google Ads / Meta Ads standard for variant id
//        c) `?utm_campaign=...`    — fallback if your ads team prefers this slot
//
//   5. Conversions: every variant that's actually shown fires a
//      `feature_variant_shown` analytics event with {page, variant} —
//      pair that with the existing `feature_cta_click` event to
//      compute variant-level conversion in GA4.
//
// Notes on tone (keep variants on-brand):
//   • Headlines: punchy, 5–9 words ideal, no jargon, no exclamation points.
//   • Body: calm and reassuring, max 2-3 sentences, ends on a benefit.
//   • Eyebrow: 1-2 words, uppercase tag (default eyebrow shown if omitted).
//
// The default copy lives in FeaturePages.jsx — variants here override it
// per visitor only when a matching UTM parameter is present.

const FEATURE_PAGE_VARIANTS = {
  // -----------------------------------------------------------------------
  // /features  (overview hub)
  // -----------------------------------------------------------------------
  overview: {
    // Punchy "stop fighting" angle for paid search traffic.
    "stop-fighting": {
      headline: "Stop fighting. Start logging.",
      body: "Five focused tools that turn day-to-day co-parenting into a single shared record — calmer for you, clearer for the court if it ever comes to that.",
    },
    // Reassurance / mediator-referral angle.
    "mediator-referral": {
      eyebrow: "Recommended by mediators",
      headline: "The calm app your mediator wishes you had.",
      body: "Designed with separated parents, mediators and family-law solicitors. Every action timestamped, every conversation on the record, every export ready for the court.",
    },
  },

  // -----------------------------------------------------------------------
  // /features/calendar
  // -----------------------------------------------------------------------
  calendar: {
    "school-pickup": {
      headline: "End the 5pm pickup arguments.",
      body: "A shared calendar that proves who agreed to what, when. Every handover, every school run, timestamped — no more 'I never said that'.",
    },
    "holiday-planning": {
      eyebrow: "Holidays & half-terms",
      headline: "Plan the summer without 200 WhatsApps.",
      body: "Propose holiday dates, get them approved, lock them in. CoParent Console is the only place the schedule lives — and the only place it changes.",
    },
  },

  // -----------------------------------------------------------------------
  // /features/expenses
  // -----------------------------------------------------------------------
  expenses: {
    "receipt-vault": {
      eyebrow: "Receipt vault",
      headline: "Every receipt. One place. Forever.",
      body: "Snap a photo at the till, tag the expense, and the split is calculated automatically. When you hand it to your solicitor, nothing is missing.",
    },
    "monthly-statement": {
      headline: "A clean statement, every month.",
      body: "Logged. Settled. Outstanding. CoParent Console keeps your shared spend organised so the next conversation isn't another disagreement about €23.",
    },
  },

  // -----------------------------------------------------------------------
  // /features/messaging
  // -----------------------------------------------------------------------
  messaging: {
    "court-admissible": {
      eyebrow: "Court-admissible",
      headline: "Built for the messages that matter.",
      body: "Tamper-evident chat designed for separated parents. Every message hashed at send, timestamped, child-scoped, and exportable as a court-ready record.",
    },
    "no-more-screenshots": {
      headline: "Replace WhatsApp screenshots for good.",
      body: "Stop screenshotting threads to prove a point. CoParent Console keeps every message in order, in full, with a fingerprint your solicitor can verify.",
    },
  },

  // -----------------------------------------------------------------------
  // /features/court-bundle
  // -----------------------------------------------------------------------
  "court-bundle": {
    "solicitor-pitch": {
      eyebrow: "For Irish family law",
      headline: "The bundle your solicitor would build by hand.",
      body: "One signed PDF per child: messages, custody history, expenses, journal, audit log. SHA-256 fingerprinted and QR-verified — solicitor-ready in under a minute.",
    },
    "evidence-export": {
      headline: "Evidence, in one click.",
      body: "Generate the exact document a family-law solicitor wants, for any date range, with every receipt and every message in order. No spreadsheet. No copy-paste. No missing pages.",
    },
  },

  // -----------------------------------------------------------------------
  // /features/journal
  // -----------------------------------------------------------------------
  journal: {
    "concerning-incident": {
      eyebrow: "Same-day notes",
      headline: "Write it down today. Remember it years later.",
      body: "Late pickups, missed handovers, doctor's notes — log them the same day they happen. Solicitors and mediators value contemporaneous notes.",
    },
    "private-record": {
      headline: "Your record. Your rules.",
      body: "Keep entries private to yourself, or share with your co-parent when the time is right. Photos and receipts attach in seconds, and nothing is shared unless you say so.",
    },
  },
};

export default FEATURE_PAGE_VARIANTS;
